import React, { useEffect, useMemo, useState } from 'react';
import { Scale, Search, Save, Loader2, RefreshCw } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import { useRestaurant } from '../../context/RestaurantContext';
import apiClient from '../../services/api';

/* Buffet → Dish Quantities: the REGISTER of buffet portions, the same idea
 * as the Live Counters register but for dishes and without ingredients.
 * One row per dish — portion per adult and kid, uptake, cost — defined once
 * here instead of retyped on every buffet's sheet. A buffet snapshots the
 * register when it is saved, so a register edit reaches each buffet on its
 * next save and never rewrites one behind its back; the day's actual
 * quantities (covers × uptake × portion + buffer) are still worked out per
 * opening, as before.
 */

const DEFAULTS = { unit: 'g', portion_g: 120, portion_pcs: 1, kid_portion_g: null, kid_portion_pcs: null, uptake_pct: 80, cost_per_kg: null, kids_ok: true };
// Counted rather than weighed — the guess only sets the default; every row can switch.
const PIECE_WORDS = /\bbottled?\b|\bcanned\b|\bnaan\b|\broti\b|\bparatha\b|\bkulcha\b|\bpuri\b|\bpoori\b|\bbhatura\b|\bchapati\b|\bpapad\b|\bidli\b|\bvada\b|\bsamosa\b|\bmomos?\b|\bkulfi\b|\bdosa\b/i;

export default function BuffetDishQuantities() {
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  const [dishNames, setDishNames] = useState(null);   // every dish the kitchen serves
  const [plans, setPlans] = useState({});             // name -> plan (register + edits)
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [dishRes, planRes] = await Promise.all([
        apiClient.get(`/restaurant/${restaurantId}/getAllDishes/`).catch(() => null),
        apiClient.get('/buffet-dish-plans/', { params: { restaurant_id: restaurantId } }).catch(() => null),
      ]);
      const names = [...new Set(((dishRes?.data?.data) || [])
        .map((d) => String(d.name || '').trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      setDishNames(names);
      setPlans(planRes?.data?.success ? (planRes.data.data || {}) : {});
      setDirty(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [restaurantId]);

  const planFor = (name) => ({
    ...DEFAULTS,
    ...(PIECE_WORDS.test(name) ? { unit: 'pcs' } : {}),
    ...(plans[name] || {}),
  });
  const setField = (name, patch) => {
    setPlans((p) => ({ ...p, [name]: { ...planFor(name), ...patch } }));
    setDirty(true);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // Every visible dish rides along, so the register covers the menu whole.
      const payload = {};
      (dishNames || []).forEach((n) => { payload[n] = planFor(n); });
      const { data } = await apiClient.put('/buffet-dish-plans/', {
        restaurant_id: restaurantId, plans: payload,
      });
      if (!data?.success) throw new Error(data?.error || 'Failed to save');
      setPlans(data.data || {});
      setDirty(false);
      toast({ title: 'Register saved', description: 'Buffets take these portions on their next save.' });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => (dishNames || [])
    .filter((n) => !search || n.toLowerCase().includes(search.toLowerCase())),
  [dishNames, search]);

  return (
    <div className="p-6 w-full">
      <header className="mb-4">
        <h1 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground flex items-center gap-2">
          <Scale className="h-6 w-6 text-amber-600" /> Dish Quantities
        </h1>
        <p className="mt-1 text-sm text-muted-foreground max-w-3xl">
          The buffet register of portions — set each dish once (portion per adult and kid, uptake,
          cost) and every buffet takes these when it is saved. The day's actual quantity is still
          covers × uptake × portion, plus the buffet's safety buffer, worked out per opening.
        </p>
      </header>

      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="relative">
          <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dishes..." className="pl-8 w-72" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={load} disabled={loading}
            title="Reload" aria-label="Reload">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={saveAll} disabled={saving || !dirty}>
            {saving ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Save size={15} className="mr-1.5" />}
            {saving ? 'Saving…' : dirty ? 'Save register' : 'Saved'}
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">Dish</th>
                <th className="px-4 py-2 font-medium whitespace-nowrap">Portion / adult</th>
                <th className="px-4 py-2 font-medium whitespace-nowrap"
                  title="What a child is served; blank = half the adult portion, 0 = not served to kids">
                  Portion / kid
                </th>
                <th className="px-4 py-2 font-medium"
                  title="Share of guests who actually take this dish">
                  Uptake
                </th>
                <th className="px-4 py-2 font-medium whitespace-nowrap">Cost (Rs)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading dishes…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No dishes match. Dishes are created under Menu &amp; Dishes.
                </td></tr>
              ) : rows.map((name) => {
                const p = planFor(name);
                const counted = p.unit === 'pcs';
                return (
                  <tr key={name} className="border-b border-border/60">
                    <td className="px-4 py-2 text-foreground">{name}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <input type="number" min={counted ? 1 : 10} step={counted ? 1 : 10}
                        value={counted ? p.portion_pcs : p.portion_g}
                        onChange={(e) => setField(name, counted
                          ? { portion_pcs: Math.max(1, Math.round(Number(e.target.value) || 1)) }
                          : { portion_g: Number(e.target.value) || 0 })}
                        className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                      <select value={p.unit}
                        onChange={(e) => setField(name, { unit: e.target.value })}
                        className="ml-1.5 rounded border border-border bg-background px-1.5 py-1 text-xs text-muted-foreground">
                        <option value="g">g</option>
                        <option value="pcs">pcs</option>
                      </select>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <input type="number" min="0" step={counted ? 1 : 10}
                        value={(counted ? p.kid_portion_pcs : p.kid_portion_g) ?? ''}
                        placeholder={String(counted ? Math.max(1, Math.round(p.portion_pcs / 2)) : Math.round(p.portion_g / 2))}
                        onChange={(e) => setField(name, counted
                          ? { kid_portion_pcs: e.target.value === '' ? null : Math.round(Number(e.target.value) || 0) }
                          : { kid_portion_g: e.target.value === '' ? null : Number(e.target.value) || 0 })}
                        className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                      <span className="ml-1 text-xs text-muted-foreground">{p.unit}</span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <input type="number" min="0" max="100" step="5" value={p.uptake_pct}
                        onChange={(e) => setField(name, { uptake_pct: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
                        className="w-16 rounded border border-border bg-background px-2 py-1 text-sm" />
                      <span className="ml-1 text-xs text-muted-foreground">%</span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <input type="number" min="0" step="10" value={p.cost_per_kg ?? ''} placeholder="—"
                        onChange={(e) => setField(name, { cost_per_kg: e.target.value === '' ? null : Number(e.target.value) })}
                        className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                      <span className="ml-1 text-xs text-muted-foreground">per {counted ? 'pc' : 'kg'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
